/**
 * <discount-collection> — renders every VARIANT in a collection that currently has a discount,
 * fetched client-side through the Storefront GraphQL API (so it is not limited to the 50-product
 * Liquid ceiling and can paginate by variant).
 *
 * A variant is shown when its `custom.discount` boolean metafield is true (blank/unset -> false).
 * Each card shows only variant.price (no struck-through original, no savings badge).
 *
 * Money note: the Storefront API returns money amounts as decimal strings in MAJOR units (e.g.
 * "19.99"), unlike Liquid which uses cents. We multiply by 100 before formatting with the shop
 * money format (which expects cents).
 *
 * Config is provided by a child <script type="application/json"> element. Routes / cartType are
 * read from window.themeVariables at runtime to match the theme's own add-to-cart flow.
 */
(function () {
  if (window.customElements.get('discount-collection')) return;

  // Tokenized Storefront access has no fixed query-cost cap (the 1000 limit is for tokenless
  // access only), so we fetch as much as the API allows per call to minimise round-trips.
  // 250 is the Storefront connection maximum for `first`.
  var PRODUCTS_PER_REQUEST = 50;  // products fetched per GraphQL request
  var VARIANTS_PER_PRODUCT = 250; // variants per product — 250 fully covers a product's variants in one call
  var IMAGE_WIDTHS = [200, 300, 400, 500, 600, 700, 800, 1000];

  var PRODUCTS_QUERY = [
    'query DiscountVariants($handle: String!, $cursor: String, $products: Int!, $variants: Int!) {',
    '  collection(handle: $handle) {',
    '    products(first: $products, after: $cursor) {',
    '      pageInfo { hasNextPage endCursor }',
    '      nodes {',
    '        title',
    '        handle',
    '        tags',
    '        onlineStoreUrl',
    '        featuredImage { url width height altText }',
    '        variants(first: $variants) {',
    '          nodes {',
    '            id',
    '            title',
    '            availableForSale',
    '            price { amount currencyCode }',
    '            image { url width height altText }',
    '            discount: metafield(namespace: "custom", key: "discount") { value }',
    '          }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Canonical Shopify money formatter. `cents` is an integer number of cents.
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = (format || '').match(placeholderRegex);
    var placeholder = match ? match[1] : 'amount';
    switch (placeholder) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', '.'); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' ', '.'); break;
      case 'amount_no_decimals_with_apostrophe_separator': value = formatWithDelimiters(cents, 0, "'", '.'); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return (format || '{{amount}}').replace(placeholderRegex, value);
  }

  function imageUrl(url, width) {
    if (!url) return '';
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('width', width);
      return u.href;
    } catch (e) {
      return url;
    }
  }

  function variantNumericId(gid) {
    return String(gid || '').split('/').pop();
  }

  var DiscountCollection = class extends HTMLElement {
    connectedCallback() {
      var configEl = this.querySelector('script[type="application/json"]');
      if (!configEl) return;
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        return;
      }

      this.resultsEl = this.querySelector('[data-discount-results]');
      this.loadMoreBtn = this.querySelector('[data-discount-load-more]');
      this.footerEl = this.querySelector('[data-discount-footer]');
      this.statusEl = this.querySelector('[data-discount-status]');

      this.seen = new Set();     // variant ids already shown — guards against any duplicate across pages
      this.cursor = null;        // Storefront pagination cursor — persisted so the next fetch resumes where we stopped
      this.hasNextPage = true;   // whether the collection still has more products to scan
      this.pending = [];         // CACHE: discounted variants fetched but not yet shown — carried across "Load more" clicks so they are never refetched or wasted
      this.renderedCount = 0;
      this.loading = false;

      this.canLoadMore = !!this.config.token;

      var self = this;
      if (this.loadMoreBtn) {
        this.loadMoreBtn.addEventListener('click', function () { self.loadMore(); });
      }
      if (this.resultsEl) {
        this.resultsEl.addEventListener('click', function (e) { self._onResultsClick(e); });
      }
      // Everything is loaded by JS — fetch and render the first batch on connect.
      this.loadMore();
    }

    async loadMore() {
      if (this.loading || !this.config || !this.canLoadMore) return;
      this.loading = true;
      this._setBusy(true);

      try {
        var perLoad = this.config.variantsPerLoad || 50;
        // Keep fetching pages (advancing the cursor) until the cache holds a full batch of
        // discounted variants, or the collection runs out. A single request can return more
        // valid variants than one batch needs — the surplus stays in `pending` for next time.
        while (this.pending.length < perLoad && this.hasNextPage) {
          await this._fetchNextPage();
        }
        // Show one batch; the leftover remains cached in `pending` for the next click.
        var batch = this.pending.splice(0, perLoad);
        if (batch.length) {
          this._appendCards(batch);
          this.renderedCount += batch.length;
        }
        this._updateUi();
      } catch (err) {
        if (window.console && console.error) console.error('[discount-collection]', err);
        this._showError();
      } finally {
        this.loading = false;
        this._setBusy(false);
      }
    }

    async _fetchNextPage() {
      var endpoint = 'https://' + this.config.shopDomain + '/api/' + this.config.apiVersion + '/graphql.json';
      var body = {
        query: PRODUCTS_QUERY,
        variables: {
          handle: this.config.collectionHandle,
          cursor: this.cursor,
          products: PRODUCTS_PER_REQUEST,
          variants: VARIANTS_PER_PRODUCT
        }
      };

      var json = await this._postWithRetry(endpoint, body);
      var collection = json && json.data && json.data.collection;
      if (!collection || !collection.products) {
        this.hasNextPage = false;
        return;
      }

      var conn = collection.products;
      this.cursor = conn.pageInfo.endCursor;
      this.hasNextPage = conn.pageInfo.hasNextPage;

      var nodes = conn.nodes || [];
      for (var i = 0; i < nodes.length; i++) {
        var product = nodes[i];
        var variants = (product.variants && product.variants.nodes) || [];
        for (var j = 0; j < variants.length; j++) {
          var variant = variants[j];
          // The custom.discount boolean metafield decides whether a variant is shown. Storefront
          // returns boolean metafields as the string "true"/"false"; blank/unset -> false.
          var isDiscounted = variant.discount && variant.discount.value === 'true';
          var price = parseFloat(variant.price && variant.price.amount);
          var numericId = variantNumericId(variant.id);

          if (isDiscounted && !isNaN(price) && !this.seen.has(numericId)) {
            this.seen.add(numericId);
            this.pending.push({ product: product, variant: variant, price: price });
          }
        }
      }
    }

    async _postWithRetry(endpoint, body, attempt) {
      attempt = attempt || 0;
      var response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.config.token
        },
        body: JSON.stringify(body)
      });

      var json = await response.json().catch(function () { return null; });

      // Retry on throttling (query cost leaky-bucket) up to 3 times.
      var throttled = json && json.errors && json.errors.some(function (e) {
        return e && (e.extensions && e.extensions.code === 'THROTTLED') || /throttl/i.test(e && e.message || '');
      });
      if (throttled && attempt < 3) {
        await new Promise(function (r) { setTimeout(r, 1000 * (attempt + 1)); });
        return this._postWithRetry(endpoint, body, attempt + 1);
      }

      if (!response.ok || (json && json.errors)) {
        throw new Error('Storefront API error: ' + JSON.stringify(json && json.errors || response.status));
      }
      return json;
    }

    _appendCards(batch) {
      var html = '';
      for (var i = 0; i < batch.length; i++) {
        html += this._cardHtml(batch[i]);
      }
      if (this.resultsEl) this.resultsEl.insertAdjacentHTML('beforeend', html);
    }

    _cardHtml(entry) {
      var cfg = this.config;
      var product = entry.product;
      var variant = entry.variant;
      var priceCents = Math.round(entry.price * 100);

      var numericId = variantNumericId(variant.id);
      var base = product.onlineStoreUrl || (cfg.routesRoot + 'products/' + product.handle);
      var url = base + (base.indexOf('?') === -1 ? '?' : '&') + 'variant=' + numericId;

      var image = variant.image || product.featuredImage;
      var aspect = image && image.width && image.height ? image.width / image.height : 1;

      var soldOut = variant.availableForSale === false;
      var priceFormat = cfg.currencyCodeEnabled ? cfg.moneyCurrencyFormat : cfg.moneyFormat;

      var labels = '';
      if (soldOut) {
        labels += '<span class="label label--subdued">' + escapeHtml(cfg.strings.soldOut) + '</span>';
      }
      // Show the product's "__label:" tags as badges — same markup/classes as the theme's product
      // card, so they render identically (e.g. the blue "Up to 15% OFF!" pill). Tags are product-level,
      // so every variant of a product shows the same label(s). The text after the first ":" is used.
      var tags = product.tags || [];
      for (var t = 0; t < tags.length; t++) {
        var tag = tags[t];
        if (tag.indexOf('__label') !== 0) continue;
        var colon = tag.indexOf(':');
        if (colon === -1) continue;
        var labelText = tag.slice(colon + 1).trim();
        if (!labelText) continue;
        var labelClass = tag.indexOf('__label2') === 0 ? 'label--custom2'
          : (tag.indexOf('__label3') === 0 ? 'label--custom3' : 'label--custom');
        labels += '<span class="label ' + labelClass + '">' + escapeHtml(labelText) + '</span>';
      }

      var imgHtml = '';
      if (image && image.url) {
        var srcset = IMAGE_WIDTHS.map(function (w) { return imageUrl(image.url, w) + ' ' + w + 'w'; }).join(', ');
        imgHtml =
          '<img class="product-item__primary-image" loading="lazy" ' +
          'src="' + escapeHtml(imageUrl(image.url, 600)) + '" ' +
          'srcset="' + escapeHtml(srcset) + '" ' +
          'sizes="' + escapeHtml(cfg.sizes || '') + '" ' +
          'alt="' + escapeHtml(image.altText || product.title) + '" ' +
          'width="' + (image.width || '') + '" height="' + (image.height || '') + '">';
      }

      var aspectClass = cfg.productImageSize && cfg.productImageSize !== 'natural'
        ? ' aspect-ratio--' + cfg.productImageSize : '';

      var variantTitle = '';
      if (variant.title && variant.title !== 'Default Title') {
        variantTitle = '<span class="product-item-meta__variant text--subdued"> — ' + escapeHtml(variant.title) + '</span>';
      }

      var priceList =
        '<div class="price-list price-list--centered">' +
          '<span class="price">' +
            '<span class="visually-hidden">' + escapeHtml(cfg.strings.salePrice) + '</span>' +
            formatMoney(priceCents, priceFormat) +
          '</span>' +
        '</div>';

      var cta = '';
      if (cfg.showCta) {
        if (soldOut) {
          cta = '<div class="product-item__cta-wrapper"><span class="product-item__link text--subdued">' +
            escapeHtml(cfg.strings.soldOut) + '</span></div>';
        } else {
          cta = '<div class="product-item__cta-wrapper">' +
            '<button type="button" class="product-item__cta button button--primary" data-add-to-cart data-variant-id="' +
            escapeHtml(numericId) + '">' + escapeHtml(cfg.strings.addToCart) + '</button></div>';
        }
      }

      // Quick-add on the image (when the CTA is not shown below the info): mirrors the product card's
      // hover button — a full-width "Add to cart" on desktop and a cart-icon button on touch — but adds
      // the specific variant directly instead of opening a quick-buy drawer.
      var quickAdd = '';
      if (!soldOut && !cfg.showCta) {
        quickAdd =
          '<div class="product-item__quick-form">' +
            '<button type="button" class="button button--outline button--text button--full hidden-touch" data-add-to-cart data-variant-id="' + escapeHtml(numericId) + '">' +
              escapeHtml(cfg.strings.addToCart) +
            '</button>' +
            '<button type="button" class="product-item__quick-buy-button hidden-no-touch" data-add-to-cart data-variant-id="' + escapeHtml(numericId) + '">' +
              '<span class="visually-hidden">' + escapeHtml(cfg.strings.addToCart) + '</span>' +
              (cfg.quickBuyIcon || '') +
            '</button>' +
          '</div>';
      }

      return (
        '<product-item class="product-item' + (soldOut ? ' product-item--sold-out' : '') + '">' +
          '<div class="product-item__image-wrapper">' +
            (labels ? '<div class="product-item__label-list label-list">' + labels + '</div>' : '') +
            '<a href="' + escapeHtml(url) + '" class="product-item__aspect-ratio aspect-ratio' + aspectClass + '" ' +
              'style="padding-bottom: ' + (100.0 / aspect) + '%; --aspect-ratio: ' + aspect + '">' +
              imgHtml +
            '</a>' +
            quickAdd +
          '</div>' +
          '<div class="product-item__info' + (cfg.showCta ? ' product-item__info--with-button' : '') + '">' +
            '<div class="product-item-meta">' +
              '<a href="' + escapeHtml(url) + '" class="product-item-meta__title">' +
                escapeHtml(product.title) + variantTitle +
              '</a>' +
              '<div class="product-item-meta__price-list-container">' + priceList + '</div>' +
            '</div>' +
            cta +
          '</div>' +
        '</product-item>'
      );
    }

    _onResultsClick(event) {
      var button = event.target.closest('[data-add-to-cart]');
      if (!button) return;
      event.preventDefault();
      this._addToCart(button);
    }

    async _addToCart(button) {
      var variantId = button.getAttribute('data-variant-id');
      if (!variantId) return;

      var tv = window.themeVariables || {};
      var routes = tv.routes || {};
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || this.config.routesRoot || '/';
      var cartAddUrl = (routes.cartAddUrl || (root + 'cart/add')) + '.js';
      var cartUrl = (routes.cartUrl || (root + 'cart')) + '.js';
      var cartType = (tv.settings && tv.settings.cartType) || 'drawer';

      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-busy', 'true');

      try {
        var addResponse = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }], sections: ['mini-cart'] })
        });
        var added = await addResponse.json();

        if (addResponse.ok) {
          if (cartType === 'page') {
            window.location.href = root + 'cart';
            return;
          }

          button.dispatchEvent(new CustomEvent('variant:added', {
            bubbles: true,
            detail: { variant: added.items ? added.items[0] : added }
          }));

          var cart = await (await fetch(cartUrl)).json();
          document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
          cart.sections = added.sections;
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { cart: cart, openMiniCart: cartType === 'drawer' }
          }));
        }

        // Trigger the theme's cart notification bar — same event/detail as the theme's own product
        // form (theme.js ProductForm). The global <cart-notification> in the header renders it, and
        // suppresses the success bar when cartType === "drawer" (the drawer opening is the feedback
        // there), so this is correct in every cart mode.
        document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: { status: addResponse.ok ? 'success' : 'error', error: (added && added.description) || '' }
        }));
      } catch (err) {
        if (window.console && console.error) console.error('[discount-collection] add to cart', err);
        this._showError();
      } finally {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-busy');
      }
    }

    _updateUi() {
      // Show the "Load more" footer only when there are still pages to scan or cached variants
      // waiting to be shown; hide it once the collection is exhausted.
      var moreAvailable = this.pending.length > 0 || this.hasNextPage;
      if (this.footerEl) this.footerEl.hidden = !(this.canLoadMore && moreAvailable);

      if (this.renderedCount === 0 && !moreAvailable) {
        this._setStatus(this.config.strings.empty);
      } else {
        this._setStatus('');
      }
    }

    _setBusy(isBusy) {
      if (this.loadMoreBtn) this.loadMoreBtn.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      this.toggleAttribute('loading', isBusy);
      if (isBusy && this.renderedCount === 0) this._setStatus(this.config.strings.loading);
    }

    _setStatus(message) {
      if (this.statusEl) this.statusEl.textContent = message || '';
    }

    _showError(message) {
      this._setStatus(message || this.config.strings.error);
      // Keep the footer (and its button) visible if there is still something to retry; hide it once exhausted.
      if (this.footerEl) this.footerEl.hidden = this.pending.length === 0 && !this.hasNextPage;
    }
  };

  window.customElements.define('discount-collection', DiscountCollection);
})();
